import type { DatabaseProperties } from "./database-properties";
import { database } from ":shared/database/connection";
import type { ReturnOptions, Tables } from "./types";
import { getTable } from "./get-table";
import { isArray, isEmpty } from "lodash-es";

export async function deleteRecordsByRelationId<
	T extends DatabaseProperties,
	K extends string,
>({
	id,
	key,
	options = {},
	table,
}: {
	key: K;
	id: DatabaseProperties["id"] | DatabaseProperties["id"][];
	table: Tables;
	options?: ReturnOptions;
}) {
	const idArr = isArray(id) ? id : [id];
	const records = (await getTable(table)
		.where(key)
		.anyOf(idArr)
		.toArray()) as T[];

	if (isEmpty(records)) {
		if (options.shouldSuppressError) {
			return Promise.resolve([]);
		}
		throw new Error(`Record with ${key}:${id} not found`);
	}

	if (options.returnTransaction) {
		return getTable(table).bulkDelete(records.map((r) => r.id));
	}

	await database.transaction("rw", getTable(table), async () => {
		await getTable(table).bulkDelete(records.map((r) => r.id));
	});

	return records;
}

export async function deleteRecords<
	T extends DatabaseProperties,
	K extends keyof T = keyof T,
>({
	value,
	key,
	options = {},
	table,
}: {
	key: K[];
	value: Partial<Record<K, unknown[]>>;
	table: Tables;
	options?: ReturnOptions;
}) {
	const records = (await getTable<T, string>(table)
		.filter((row) =>
			key.reduce((acc, k) => acc && (value[k] as any).includes(row[k]), true),
		)
		.toArray()) as T[];

	if (isEmpty(records)) {
		if (options.shouldSuppressError) {
			return Promise.resolve([]);
		}
		throw new Error(`Record not found`);
	}

	if (options.returnTransaction) {
		return getTable(table).bulkDelete(records.map((r) => r.id));
	}

	await database.transaction("rw", getTable(table), async () => {
		await getTable(table).bulkDelete(records.map((r) => r.id));
	});

	return records;
}
